// House rule (owner, 2026-08-10): "I want clean descriptive UI without trash on
// it." Every string here names a thing or an action and stops. Nothing explains
// why a feature exists, nothing reassures, nothing repeats what the control it
// sits next to already says. The exceptions are deliberate and few:
//
//   - the §21.4 / §9.2 search-honesty lines, which state what the archive does
//     NOT yet contain and are the whole point of the surface they sit on;
//   - errors, which say what failed and what to do about it, in one clause;
//   - aria-labels, which have to stand alone with no layout around them.
export default {
    general: {
        add:           'Add',
        remove:        'Remove',
        edit:          'Edit',
        cancel:        'Cancel',
        back:          'Back',
        save:          'Save',
        logout:        'Log out',
        enable:        'Enable',
        disable:       'Disable',
        confirm:       'Continue',
        confirm_title: 'Are you sure?',
    },
    errors: {
        generic_error:  'An error occurred',
        invalid_data:   'Invalid data',
        not_found:      'Not found',
        unauthorized:   'Not authorized',
        already_exists: 'That already exists',
        on_add:         'Error while adding',
        on_edit:        'Error while updating',
    },
    success: {
        add:  'Successfully added',
        edit: 'Successfully updated',
    },
    validation: {
        required: 'Required',
        email:    'Invalid email address',
        url:      'Must be an https:// URL',
        port:     'Must be 1–65535',
    },
    auth: {
        title:          'Jackdaw',
        subtitle:       'Sign in to your mail',
        password:       'Password',
        sign_in:        'Sign in',
        wrong_password: 'Wrong password',

        // A sign-in link carries a one-time challenge; a tab left open outlives
        // it. The page starts a fresh flow by itself and says so. The second
        // line is the loop guard speaking (views/auth/Login.vue): one restart is
        // a stale tab, two in a row is the sign-in service itself.
        restarting:          'Sign-in link expired — starting a new one',
        restart_failed:      'Couldn\'t start a new sign-in',
        restart_failed_help: 'Try again — if it keeps failing, the sign-in service is down.',
        try_again:           'Try again',

        // The passkey way in (tech spec §8.4). One line for the button, one for a
        // failure — a cancelled prompt says nothing, because the owner said no.
        sign_in_passkey: 'Sign in with passkey',
        passkey_failed:  'Passkey sign-in failed',
    },
    connections: {
        // Doubles as the Accounts section's own heading and nav label — a
        // connection IS an account, and "Accounts" is what the owner is
        // looking for from the settings nav (func spec §4).
        title:       'Accounts',
        add:         'Add connection',
        empty:       'No connections',
        name:        'Name',
        address:     'Email address',
        password:    'Password',
        folders:     'Folders',
        storage:     'Storage & sync',
        service: { mail: 'Mail', calendar: 'Calendar', contacts: 'Contacts' },

        state: { pending: 'pending', ready: 'ready', failed: 'failed', reauthenticating: 'reauthenticating', paused: 'paused', unavailable: 'unavailable' },
        role: { inbox: 'Inbox', sent: 'Sent', drafts: 'Drafts', trash: 'Trash', archive: 'Archive', junk: 'Junk', custom: 'Custom' },

        added:       'Connection added',
        add_failed:  'Could not add connection',

        // Server type, chosen first when adding an account (func spec §4.3
        // step 1).
        type: {
            open_standards: 'Open standards (IMAP/SMTP)',
            exchange:       'Exchange (on-premises)',
            microsoft365:   'Microsoft 365',
            outlook:        'Outlook.com',
        },

        // On-premises Exchange (tech spec §7.1). The URL is resolved from the
        // address's domain by Autodiscover and typed only when that finds
        // nothing; the login is the server's own form spelling where it is not
        // the address.
        ews_url:   'Server URL',
        ews_login: 'Login',
        ews_auth:  'Authentication',
        ews_auth_mode: { ntlm: 'NTLM', basic: 'Basic' },

        // The Microsoft sign-in (tech spec §7.2). The two Microsoft types have
        // no fields of their own — the credential is obtained at Microsoft.
        continue_with_microsoft: 'Continue with Microsoft',
        reauthenticate:          'Re-authenticate',
        reauthenticate_failed:   'Couldn\'t start the Microsoft sign-in',

        // What the sign-in came back with when it produced no connection —
        // ?graph_error=<code> on the accounts list. Microsoft's own codes
        // arrive here too, so anything else falls back to `unknown`.
        graph_error: {
            access_denied:    'Microsoft sign-in cancelled',
            consent_required: 'Microsoft sign-in needs consent',
            exchange_failed:  'Microsoft sign-in could not be completed',
            profile_failed:   'Couldn\'t read that Microsoft account',
            no_address:       'That Microsoft account has no email address',
            address_mismatch: 'That is a different mailbox',
            unknown:          'Microsoft sign-in failed',
        },

        // Shared mailboxes (func spec §4.1, §4.3 step 4) — a peer of the
        // owner's own mailbox on the same connection.
        shared_mailboxes:      'Shared mailboxes',
        shared_added:          'Mailbox added',
        shared_add_failed:     'Couldn\'t add that mailbox',
        shared_remove:         'Remove',
        shared_remove_confirm: 'Remove {address}? Its local archive is erased.',
        shared_removed:        'Mailbox removed',
        shared_remove_failed:  'Couldn\'t remove that mailbox',

        // Reconnect (func spec §4.4, §25). Each service is reported on its own,
        // because mail can come back while calendar does not.
        reconnect:         'Reconnect',
        reconnect_ok:      'Reconnected',
        reconnect_partial: 'Reconnected — still failing: {services}',
        reconnect_failed:  'Couldn\'t reach that account',

        // The account row's own state marker (func spec §4.4), condensed to one
        // word from the three independent service statuses — see
        // connectionStateKey in libs/connection.ts.
        marker: {
            active:   'Active',
            degraded: 'Degraded',
            // §4.4's Reauthenticating: amber like Degraded, and a different
            // thing to do about it.
            reauthenticating: 'Needs sign-in',
            failed:           'Failed',
            paused:           'Paused',
            disconnected:     'Disconnected',
        },
        unread_summary: '{count} unread',

        // Editing a connection (func spec §4.4's Edit action). display_name
        // reuses the "Name"/"Email address" labels above — one form vocabulary
        // for adding and editing.
        edit_title:       'Edit account',
        password_keep:    'Leave blank to keep the current password',
        imap:             'IMAP',
        smtp:             'SMTP',
        host:             'Host',
        port:             'Port',
        security:         'Security',
        save_failed:      'Couldn\'t save that change',

        // The hand-entered endpoints on the add form (func spec §4.3 step 2,
        // tech spec §7.3). Collapsed until discovery comes back with nothing,
        // which is when the last line is shown above them.
        server_settings:   'Server settings',
        imap_server:       'IMAP server',
        smtp_server:       'SMTP server',
        caldav_url:        'CalDAV URL',
        carddav_url:       'CardDAV URL',
        security_tls:      'TLS',
        security_starttls: 'STARTTLS',
        no_mail_server:    'No mail server found for {domain}. Enter it below.',
        // The Exchange twin (tech spec §7.1): Autodiscover found nothing, so the
        // URL is typed after all.
        no_exchange_server:   'No Exchange server found for {domain}. Enter the URL below.',
        credentials_refused:  'The server rejected the username or password',

        // Pause/resume (func spec §4.4's Paused state — "owner suspended
        // sync").
        pause:        'Pause',
        resume:       'Resume',
        paused_ok:    'Sync paused',
        resumed_ok:   'Sync resumed',
        pause_failed: 'Couldn\'t change that',

        // Removing (func spec §4.6 — "never deletes archived content" unless
        // asked, in plain language, twice).
        remove:                 'Remove',
        disconnect:             'Disconnect',
        disconnect_confirm:     'Disconnect {address}? The archive stays readable.',
        disconnect_ok:          'Disconnected',
        disconnect_failed:      'Couldn\'t disconnect that account',
        disconnect_and_erase:   'Disconnect and erase',
        erase_warning:          'Permanently destroys the local archive for {address}. This cannot be undone.',
        erase_type_prompt:      'Type {address} to confirm.',
        erase_mismatch:         'Doesn\'t match',
        erase_ok:               'Erased',
        erase_failed:           'Couldn\'t erase that account',
    },
    password: {
        // Doubles as the section heading and its nav label.
        title:   'Password',
        current: 'Current password',
        new:     'New password',
        confirm: 'Confirm new password',

        changed:   'Password changed',
        wrong:     'Wrong password',
        mismatch:  'Passwords do not match',
        too_short: 'At least 12 characters',
    },
    passkeys: {
        // Doubles as the section heading and its nav label.
        title:  'Passkeys',
        add:    'Add passkey',
        rename: 'Rename',
        name:   'Name',
        empty:  'No passkeys',

        // The row's second line. The dates are what tell one device from another once
        // there are several, and `synced` is the platform's own backup state.
        added:      'Added {date}',
        last_used:  'last used {date}',
        never_used: 'never used',
        synced:     'synced',

        added_toast:    'Passkey added',
        add_failed:     'Passkey couldn\'t be added',
        rename_failed:  'Passkey couldn\'t be renamed',
        remove_confirm: 'Remove {name}?',
        remove_failed:  'Passkey couldn\'t be removed',
    },
    settings: {
        title:             'Settings',
        back_to_mail:      'Back to mail',
        reading:           'Reading',
        sending:           'Sending',
        search:            'Search',

        // The Archive section's standing backfill watches (func spec §21.4) —
        // invisible once created before this section existed.
        watches_title:  'Watches',
        watches_empty:  'No standing watches',
        watch_cancel:   'Cancel watch',
        watch_hit:      'Found — the archive reached this date',
        watch_cancelled: 'Watch cancelled',
        archive_by_root: 'By account',

        // Only the zero cases are worded: 0 in a delay field reads as "instantly"
        // and means the opposite here, and nothing else about either number is
        // ambiguous (func spec §10.1, §11.7).
        mark_read_delay: 'Mark as read after (ms)',
        mark_read_off:   '0 = never',

        // Func spec §25's "Prefer plain text | Off" — the standing default behind
        // §10.2's per-message switch.
        prefer_plain_text: 'Prefer plain text',

        undo_seconds: 'Undo window (seconds)',
        undo_off:     '0 = send immediately',

        // The default identity for new messages (func spec §25, §11.2). The
        // empty option is §25's own wording for the default.
        default_identity:       'Default identity',
        default_identity_first: 'First root',

        // Remote content (func spec §10.2, §25).
        remote_content:      'Remote content',
        load_remote_images:  'Load remote images',
        remote_never:        'Never',
        remote_always:       'Always',
        allowed_senders:     'Allowed senders',
        sender_address:      'Email address',

        // The archive (func spec §8.6). Zero is the only value that needs a
        // word: an empty threshold reads as "warn at nothing" and means the
        // opposite.
        archive:             'Archive',
        storage_warning:     'Warn above (GB)',
        storage_warning_off: '0 = off',

        save_failed: 'Couldn\'t save that setting',
    },
    // Appearance (func spec §25 — Light and Dark, no System). The settings
    // section: its own name, and the one switch on it.
    appearance: {
        title: 'Appearance',
        dark:  'Dark',
    },
    // The sun/moon toggle (components/ColorModeToggle.vue): the tooltip names
    // the mode a click switches TO, so the button says what it will do.
    color_mode: {
        switch_to_light: 'Switch to light',
        switch_to_dark:  'Switch to dark',
    },
    // The keyboard model (func spec §23.5). One label per action id
    // (ShortcutAction), read by the reference card and by the settings rows that
    // rebind them — the binding itself is never written here, because it is the
    // owner's to change.
    shortcuts: {
        title: 'Shortcuts',
        // Between the two keys of a chord: "g then i".
        then:  'then',
        press: 'Press keys',
        reset: 'Reset',

        next:             'Next message',
        prev:             'Previous message',
        expand_thread:    'Expand conversation',
        collapse_thread:  'Collapse conversation',
        extend_next:      'Extend selection down',
        extend_prev:      'Extend selection up',
        select_row:       'Select or deselect',
        select_all:       'Select all',
        open:             'Open',
        back:             'Back to list',
        reply:            'Reply',
        reply_all:        'Reply all',
        forward:          'Forward',
        compose:          'Compose',
        archive:          'Archive',
        delete:           'Delete',
        flag:             'Flag',
        delete_permanent: 'Delete permanently',
        toggle_read:      'Toggle read',
        move:             'Move to folder',
        undo:             'Undo',
        search:           'Search',
        go_inbox:         'Go to Inbox',
        go_sent:          'Go to Sent',
        go_drafts:        'Go to Drafts',
        go_trash:         'Go to Trash',
        send:             'Send',
        dismiss:          'Close or cancel',
        help:             'Shortcut reference',
    },
    // The search reference (func spec §21.3). Only the heading and the two
    // column heads are worded here — the terms themselves are data
    // (@/libs/searchSyntax) and are typed, not translated.
    search_syntax: {
        title: 'Search terms',
        term:  'Term',
        value: 'Value',
    },
    // Saved searches (func spec §21.5). The sidebar row is the name alone, so
    // everything here names an action or a field and stops; the one sentence is
    // the delete confirmation, which has to say what is about to go.
    saved: {
        title:      'Saved',
        save:       'Save search',
        name:       'Name',
        scope:      'Scope',
        pin:        'Pin',
        unpin:      'Unpin',
        rename:     'Rename',
        delete:     'Delete',
        move_up:    'Move up',
        move_down:  'Move down',
        actions:    'Saved search actions',
        empty:      'No saved searches',
        name_taken: 'Name already used',
        created:    'Search saved',
        renamed:    'Renamed',
        delete_confirm: 'Delete “{name}”?',
        save_failed:    'Couldn\'t save that search',
        delete_failed:  'Couldn\'t delete that search',
    },
    identities: {
        title:            'Identities',
        address:          'Email address',
        display_name:     'Display name',
        add_alias:        'Add alias',
        remove:           'Remove',
        removed:          'Alias removed',
        added:            'Alias added',
        no_roots:         'No accounts — add a connection first',
        // reply_match (func spec §4.5, §11.2) is a HEADING over a switch whose
        // label is the identity's own address, so the control reads "Replies to
        // mail sent to · alias@example.com" — which is the whole rule. Its old
        // wording ("Use for replies to this address") named neither the mail
        // that is matched nor the address it is matched on, and the owner
        // reported it as meaning nothing.
        reply_match:      'Replies to mail sent to',
        duplicate:        'This account already sends from that address',
        confirm_remove:   'Remove {address} as a sending address?',
        remove_failed:    'Couldn\'t remove that alias',
        update_failed:    'Couldn\'t save that change',
        kind: { primary: 'Primary', alias: 'Alias', send_as: 'Send as' },
    },
    signatures: {
        title:            'Signatures',
        new:              'New',
        edit:             'Edit signature',
        delete:           'Delete',
        deleted:          'Signature deleted',
        delete_failed:    'Couldn\'t delete that signature',
        empty:            'No signatures',
        name:             'Name',
        formatted:        'Formatted',
        plain:            'Plain text',
        auto_generated:   'Generated from the formatted version',
        edited_by_hand:   'Edited by hand',
        none:             'None',
        unused:           'Not assigned to any identity',
        used_by:          'Used by {addresses}',
        // The three ASSIGNMENT controls on an identity (func spec §11.5). They
        // sit under the "Signatures" heading above, which is what makes them
        // legible: on their own, none of the three says what it is choosing
        // (owner report: "3 dropdowns are for signatures and neither the name
        // nor the segment signals that we are selecting signature settings").
        slot_new:         'New messages',
        slot_reply:       'Replies and forwards',
        placement:        'Placement',
        placement_above:  'Above quote',
        placement_below:  'Below quote',
        confirm_delete:   'Delete "{name}"?',

        // The Signatures section's detail pane before a row is chosen.
        pick:             'Select a signature',

        // The full-width editor panel (owner feedback, 2026-08-19 — signature
        // editing singled out as cramped). Preview shows the signature against
        // both grounds it is actually read on: the owner's own client and
        // whatever the recipient's happens to be.
        preview:       'Preview',
        preview_light: 'Light',
        preview_dark:  'Dark',

        // §11.5's own warning, said once and only when it is true — an image in
        // a signature is sent with every message that uses it.
        image_warning: 'Attached to every message that uses this signature.',

        // Func spec §11.5: changing identity or signature "never silently
        // discards a signature the owner has edited by hand: it asks".
        replace:               'Replace it',
        confirm_replace_title: 'You edited this signature',
        confirm_replace:       'Replace it with the other one, or keep what you wrote?',
    },
    // The Main view and its unified companions (func spec §5.2). Every label
    // names the list it opens; the editor's two lines are counts, which is what
    // §5.2.3 asks the tree to show.
    main: {
        title:   'Main',
        sent:    'Sent',
        drafts:  'Drafts',
        flagged: 'Flagged',

        editor_title:  'Main view',
        editor_counts: '{total} · {unread} unread',
        editor_total:  'In Main: {folders} folders · {unread} unread',

        save_failed: 'Couldn\'t save that change',
    },
    folders: {
        new:      'New folder',
        new_sub:  'New subfolder',
        rename:   'Rename',
        delete:   'Delete',
        name:     'Name',
        pin:      'Pin',
        unpin:    'Unpin',
        hide:     'Hide',
        unhide:   'Show',
        role:     'Role',
        role_automatic: 'Automatic',
        role_auto_was:  'Automatic ({role})',
        // The ACCOUNT's icon and colour (func spec §5.3). It lives under
        // `folders` because that is where the sidebar's vocabulary is; folders
        // themselves carry neither any more (owner decision 2026-08-30).
        marker:   'Icon & colour',
        icon:     'Icon',
        colour:   'Colour',
        clear:    'Clear',
        default:  'Default',
        expand:   'Expand',
        collapse: 'Collapse',

        character: 'Character or emoji',

        add_to_main:      'Add to Main',
        remove_from_main: 'Remove from Main',

        // Deleting is upstream and the mail inside goes with it (func spec §5.4,
        // §8.2). The sentence says where it happens, which is the part that
        // cannot be taken back from here.
        delete_confirm: 'Delete {name} on the server?',

        created: 'Folder created',
        renamed: 'Folder renamed',

        save_failed:   'Couldn\'t save that change',
        create_failed: 'Couldn\'t create that folder',
        delete_failed: 'Couldn\'t delete that folder',
    },
    mail: {
        title:             'Mail',
        pick_folder:       'Select a folder',
        pick_conversation: 'Select a conversation',
        // §24's message-list row, in its exact words: the empty state, and the
        // error state that still lists content.
        empty_folder:      'No messages in this folder',
        showing_archive:   'Can\'t reach the server. Showing your archive.',
        load_more:         'Load more',
        no_subject:        '(no subject)',
        to:                'To',
        cc:                'Cc',
        more_recipients:   '{count} more',

        // A name in an open message's header.
        copy_address:   'Copy address',
        address_copied: 'Address copied',
        copy_failed:    'Could not copy',
        search_mail:    'Search mail',

        attachment: 'Attachment',

        // Attachments on the read side (func spec §10.4). A part that never
        // arrived is listed and disabled rather than hidden: the message really
        // does carry it.
        attachment_open:        'Open {name}',
        attachment_download:    'Download {name}',
        attachment_unavailable: 'Not retrieved yet',
        // A part whose message content never arrived is retrievable on demand
        // (func spec §10.4); one whose message IS retrieved and still has no
        // bytes is not, and §24 forbids the two looking the same.
        attachment_fetch: 'Fetch now',
        // Parts the body shows through cid: (a signature logo) are not listed
        // until asked for.
        attachments_all: 'All attachments ({count})',

        message_count:     'Messages: {count}',

        // The chat view (owner request 2026-11-02): the toggle names the view it
        // switches TO; "to {list}" names who a message went to when that changed.
        chat_view:      'Chat view',
        mail_view:      'Mail view',
        chat_today:     'Today',
        chat_yesterday: 'Yesterday',
        chat_to:        'to {list}',
        chat_you:       'you',
        chat_full:      'Full message',
        chat_less:      'Less',
        chat_unread:    'Unread',
        deleted_hidden:    '{count} deleted messages hidden',
        body_pending:      'Not retrieved yet',
        // Func spec §9.2/§24: the placeholder is offered with the action that
        // resolves it, which jumps this message ahead of the backfill queue.
        fetch_now:         'Fetch now',
        fetch_failed:      'Could not retrieve this message',
        body_empty:        'No content',
        message_body:      'Message body',

        // Remote content (func spec §10.2): blocked by default, with a discreet
        // bar offering it for this message or standing for this sender.
        remote_blocked:      'Remote images hidden',
        show_images:         'Show',
        always_this_sender:  'Always for this sender',

        // Inline (cid:) images are the message's OWN content and are shown by
        // default; each of these is a different reason one could not be, and each
        // is shown as the placeholder's tooltip where the image would have been.
        inline_loading:     'Loading…',
        inline_pending:     'Not retrieved yet',
        inline_missing:     'Not part of this message',
        inline_unsupported: 'Not viewable here — open from attachments',
        inline_too_large:   'Too large to show — open from attachments',
        inline_failed:      'Couldn\'t load this image',

        // Reading safety (func spec §10.2). The two body forms name the form the
        // item would switch TO; View source shows the message as it arrived.
        view_source:     'View source',
        show_plain_text: 'Plain text',
        show_html:       'HTML',

        // Reading quality (func spec §10.3). The expander says which way it
        // goes; the two colour items name the rendering they switch TO.
        show_quoted:     'Show quoted text',
        hide_quoted:     'Hide quoted text',
        original_colors: 'Original colors',
        dark_colors:     'Dark colors',

        // A link whose text and destination disagree, or whose domain is written
        // in punycode. The dialog states the two values and nothing else.
        link_mismatch: 'Link mismatch',
        link_punycode: 'Punycode domain',
        link_shows:    'Shows',
        link_goes_to:  'Goes to',
        link_url:      'Address',
        link_open:     'Open',
        link_back:     'Back',

        load_error:        'Couldn\'t load',
        retry:             'Retry',
        mark_read:         'Mark as read',
        mark_unread:       'Mark as unread',
        mark_all_read:     'Mark conversation as read',
        mark_all_unread:   'Mark conversation as unread',
        flag:              'Flag',
        unflag:            'Remove flag',
        flag_all:          'Flag conversation',
        unflag_all:        'Unflag conversation',
        action_failed:     'Couldn\'t apply that change',
        action_refused:    'Couldn\'t change {count} message | Couldn\'t change {count} messages',

        // Per-message results of a bulk action (func spec §12 — results are
        // reported per message, and in a cross-root action the ones a root
        // cannot perform are "skipped and named"). Three names, then a count.
        refused_named: 'Not applied: {list}',
        refused_entry: '{subject} — {reason}',
        refused_more:  '+{count} more',

        status_not_found:     'not found',
        status_no_target:     'no folder for it',
        status_invalid_state: 'doesn\'t apply',
        status_failed:        'failed',

        // Bulk selection (func spec §12).
        select_row:      'Select',
        selected_count:  '{count} selected',
        clear_selection: 'Clear selection',
        // Taking the whole list rather than the loaded page of it, and what the
        // bar says once the cap stopped it — the count beside it is already the
        // number, so this says the only other thing there is to say.
        select_all:        'Select all',
        select_all_capped: 'the list is longer',

        reply:              'Reply',
        reply_all:          'Reply all',
        forward:            'Forward',

        archive:            'Archive',
        delete:             'Delete',
        move_to:            'Move to folder',
        copy_to:            'Copy to folder',
        // A confirmation knows which folder was picked, so it names it; its
        // button says the verb alone, the destination being in the title above
        // it (@/libs/actionLabel).
        move_to_named:      'Move to {folder}',
        copy_to_named:      'Copy to {folder}',
        move:               'Move',
        copy:               'Copy',
        junk:               'Mark as junk',
        not_junk:           'Not junk',
        delete_permanently: 'Delete permanently',
        restore_to_server:  'Restore to server',

        // Func spec §8.3's second restore: local only, nothing uploaded. The
        // badge is what a message put back this way carries afterwards.
        restore_to_view:      'Restore to view',
        undo_restore_to_view: 'Move back to Trash',
        did_restore_view:   'Restored to view',
        restore_view_failed: 'Couldn\'t restore that message to view',
        locally_restored:   'Local only',

        // Func spec §11.1's fourth starting point.
        edit_as_new:        'Edit as new',

        undo:              'Undo',
        marked_read:       'Conversation marked as read',
        marked_unread:     'Conversation marked as unread',
        flagged_all:       'Conversation flagged',
        unflagged_all:     'Flag removed from conversation',

        // The same four over a conversation holding one message: the toolbar
        // acted on a mail, so what it reports is about a mail.
        marked_read_message:   'Marked as read',
        marked_unread_message: 'Marked as unread',
        flagged_message:       'Flagged',
        unflagged_message:     'Flag removed',

        // What a placement action did, offered with an Undo (func spec §12). Each
        // one carries both forms: the same sentence answers one message filed out
        // of an expanded conversation and forty filed as a conversation.
        did_archive:  'Archived {count} message | Archived {count} messages',
        did_delete:   'Moved {count} message to Trash | Moved {count} messages to Trash',
        did_move:     'Moved {count} message | Moved {count} messages',
        did_junk:     'Marked {count} message as junk | Marked {count} messages as junk',
        did_not_junk: 'Marked {count} message as not junk | Marked {count} messages as not junk',
        did_copy:     'Copied {count} message | Copied {count} messages',
        did_place:    'Moved {count} message | Moved {count} messages',

        // Undo is answered per action, so a partial result is stated as one.
        undo_done:    'Moved {count} message back | Moved {count} messages back',
        undo_partial: 'Moved {done} messages back; {failed} couldn\'t be undone',
        undo_none:    'Couldn\'t undo {count} message | Couldn\'t undo {count} messages',
        undo_failed:  'Couldn\'t undo that',

        // An offer that outlived what it stood for: something newer happened
        // while the toast was up, and that is what is undoable now. Stated
        // rather than swallowed — a click on Undo always answers.
        undo_superseded: 'Superseded by a newer action',

        trash_title:  'Trash',
        trash_empty:  'Trash is empty',
        empty_trash:  'Empty Trash',
        purged:       'No longer on the server',
        trashed:      'Deleted',

        collapse_message: 'Collapse message',
        expand_message:   'Expand message',

        // Expanding a row in the list (func spec §7.3): the conversation's own
        // messages, in place, without opening it.
        collapse_conversation: 'Collapse conversation',
        expand_conversation:   'Expand conversation',

        // The per-message menu inside an open conversation (func spec §12 —
        // "unless the owner has expanded the conversation and selected specific
        // messages"). The labels name the scope, because the toolbar above them
        // says "Archive" about the whole conversation.
        message_actions:  'Message actions',
        archive_message:  'Archive message',
        delete_message:   'Delete message',
        move_message_to:  'Move message to',
        copy_message_to:  'Copy message to',

        // What each confirmation has to say and nothing more: the count, and —
        // for the three that reach the server — which copy survives (func spec
        // §8.4). Both forms, like the did_* toasts above: one message is the
        // ordinary case here (a row, an open conversation of one), and "1
        // messages" is not something to ask the owner to confirm.
        //
        // The ACTION is named by the title and the button, not here (owner
        // request 2026-10-31, @/libs/actionLabel), so the body is the scope.
        confirm_bulk:               '{count} message. | {count} messages.',
        confirm_delete_permanently: 'Remove {count} message from the server for good? Your copy stays here. | Remove {count} messages from the server for good? Your copies stay here.',
        confirm_empty_trash:        'Remove everything in Trash from the server for good? Your copies stay here.',
        confirm_restore:            'Upload {count} message back to this folder? The server gets a new copy, with a new date. | Upload {count} messages back to this folder? The server gets new copies, with new dates.',

        search_placeholder: 'Search mail',
        search_clear:       'Clear search',
        // The scope trail (func spec §21.1): terse crumbs — the folder leaf, the
        // root address, 'Trash', 'Main', 'Everything' — never a sentence. Only
        // the crumbs that widen are buttons, so their aria-label is the action
        // itself; the crumb text alone says the rest.
        search_scope:            'Search {scope}',
        search_scope_everything: 'Everything',
        // The two empty states §24 names by hand: "No matches" and "No matches
        // yet — mail before March 2019 is still arriving". They are different
        // answers to the same query and must never read as each other.
        search_empty:         'No matches',
        search_empty_pending: 'No matches yet — mail before {date} is still arriving',
        search_empty_early:   'No matches yet — earlier mail is still arriving',
        search_results:     'Results: {count}',
        // A grouped row whose thread is only partly matched (owner report
        // 2026-10-31): the row stands for the whole conversation, and this says
        // how much of it the query actually found.
        search_matched:     '{matched} of {total} matched',
        // A field-scoped query that read its restriction depth to the bottom: the
        // results are the first N matches, and saying so is the difference between
        // a bounded answer and a wrong one.
        search_truncated:   'Showing the first {count} matches',
        // §21.4's two sections, each with its own count — one query answered by
        // two indexes, and a single total would be a sum nothing adds up to.
        search_section_mail:     'Mail',
        search_section_calendar: 'Calendar',
        // §24's search error row. Search failing is not the mailbox failing, and
        // the second sentence is the part that says so.
        search_unavailable: 'Search is unavailable. Browsing still works.',
        // §21.4's "grouped by conversation by default, ungroupable" — the two
        // states of one control, labelled by what pressing it does.
        search_ungroup:     'Ungroup',
        search_group:       'Group',
        search_scope_trash: 'Trash',

        // The category switcher (func spec §23.4). Two strings and no more: every
        // other tab is a category NAME, which comes from the owner's own taxonomy
        // and is never translated.
        //
        // Unused on screen today — the strip is unmounted until classification
        // lands (M8) — and here because the component is.
        category_all:  'All',
        category_more: 'More categories',

        // The §21.4 offer under the dated empty-yet state, and what it becomes
        // once accepted. Terse on purpose: the sentence above already says why
        // there is nothing here.
        search_watch:        'Notify me',
        search_watch_set:    'Will notify',
        search_watch_ready:  'Mail for “{query}” has arrived',
        search_watch_search: 'Search',
        search_watch_gone:   'That watch is no longer standing',

        // The §21.4 note, in the words func spec §9.2 gives it. The date is real
        // — it is the oldest message actually held — and the note stands for as
        // long as the archive is incomplete. These three are the search-honesty
        // statements and stay whole: they say what a result does NOT cover.
        search_reach_back: 'Searching mail from {date} onward — earlier mail is still being retrieved.',
        search_retrieving: 'Earlier mail is still being retrieved.',
        search_index_lag:  'Search index is about {duration} behind — the newest mail may not be findable yet.',

        // Per-root sync progress (func spec §9.3). A root that holds everything
        // shows none of this.
        phase_connecting: 'Connecting',
        phase_retrieving: 'Retrieving history',
        progress_counts:  '{retrieved} of {known}',
        // §9.3 requires the completion be "given as a range and marked as an
        // estimate". The word marks it; nothing else needs saying, so nothing
        // else is said.
        progress_estimate: 'Estimate: {duration}',
        progress_reach:    'Back to {date}',
        // Shown whenever a root has unretrievable messages, even once it is
        // otherwise complete (func spec §26): a permanent failure is a hole the
        // backfill stepped over, so a "finished" root that hides it would be lying.
        // It is a button: the count alone said nothing about what or why.
        progress_failed:   '{failed} not retrieved',

        // The list behind that line (func spec §26 — "listed with the reason;
        // retryable"). One row per message: where it belongs, when it first
        // failed, why, and how often it has been tried.
        failed_title:    'Not retrieved',
        failed_attempts: '{count} try | {count} tries',
        failed_next:     'Next try {time}',
        failed_retry:    'Retry',
        failed_result:   '{cleared} of {retried} retrieved',
        failed_error:    'Retry failed',
        // Graph is throttling the transfer (func spec §9.3, tech spec §7.2).
        // Named rather than hidden: a root that has visibly stopped moving is
        // worse unexplained.
        progress_rate_limited: 'Rate limited by Microsoft until {time}',

        // Archive size (func spec §8.6, §26). The warning is the one place a
        // sentence is spent on reassurance, because §26 fixes what it must say:
        // the threshold was passed, sync continues, nothing is deleted for it.
        storage_line:    '{total} · {shared} shared',
        storage_total:   'Archive: {total}',
        storage_warning: 'Storage over {threshold}. Sync continues; nothing is deleted.',

        duration_minutes: '{count} minute | {count} minutes',
        duration_hours:   '{count} hour | {count} hours',
        duration_days:    '{count} day | {count} days',
        // The unit is carried by {high}; {low} is a bare number when both ends
        // share one, and a full quantity when they do not.
        duration_range: '{low}–{high}',
    },
    // The rich-text editor's controls (func spec §11.4 — "rich text with a
    // deliberately restrained set"; the sizes, colours and lists are the owner's
    // request of 2026-08-10). The size steps are named for what they do to the
    // text rather than for the heading levels they are made of.
    editor: {
        size:         'Text size',
        size_normal:  'Normal',
        size_large:   'Large',
        size_larger:  'Larger',
        size_largest: 'Largest',

        bold:      'Bold',
        italic:    'Italic',
        underline: 'Underline',
        strike:    'Strikethrough',

        color:          'Text color',
        color_default:  'Default',
        color_grey:     'Grey',
        color_graphite: 'Graphite',
        color_red:      'Red',
        color_orange:   'Orange',
        color_amber:    'Amber',
        color_green:    'Green',
        color_teal:     'Teal',
        color_sky:      'Sky',
        color_blue:     'Blue',
        color_indigo:   'Indigo',
        color_violet:   'Violet',
        color_pink:     'Pink',

        bullet_list:  'Bulleted list',
        ordered_list: 'Numbered list',
        quote:        'Quote',
        code_block:   'Code block',
        rule:         'Divider',

        link:        'Link',
        link_url:    'Link address',
        link_apply:  'Apply',
        link_remove: 'Remove link',

        image:           'Insert image',
        image_too_large: 'Image too large — max {max}',

        clear_formatting: 'Clear formatting',
    },
    // The §20 invitation card, in the reading pane. Terse and descriptive: the
    // card states what the message is, when the meeting is, what changed and
    // what it collides with — and offers the three answers. Nothing explains
    // what an invitation is.
    invitation: {
        title_request:         'Invitation',
        title_update:          'Updated invitation',
        title_cancelled:       'Cancelled',
        // A reply carries somebody else's answer. Which one it was is read off
        // the meeting's attendee row, which the reply is what moved; without one
        // the card says only that they answered.
        title_reply:           '{name} replied',
        title_reply_accepted:  'Accepted by {name}',
        title_reply_declined:  'Declined by {name}',
        title_reply_tentative: 'Tentative from {name}',
        title_counter:         'Proposed a new time',
        someone:               'Someone',

        stale:    'Out of date',
        pending:  'Syncing…',
        no_event: 'Not on your calendar',
        no_title: '(no title)',

        organizer: 'Organiser',
        proposed:  'Proposed: {when}',
        // §20.5: the conflict is named before the answer is given.
        conflict:  'Conflicts with {title}, {when}',

        // §20.3's "what changed". Start and end are one line — a meeting that
        // moved moved both.
        changed_time:        'Time changed',
        changed_repeat:      'Repeat changed',
        changed_title:       'Title changed',
        changed_location:    'Location changed',
        changed_description: 'Description changed',
        changed_attendees:   'Attendees changed',

        response_accepted:     'Accepted',
        response_tentative:    'Tentative',
        response_declined:     'Declined',
        response_needs_action: 'Needs action',
        status_cancelled:      'Cancelled',
        repeats:               'Repeats',

        accept:    'Accept',
        tentative: 'Tentative',
        decline:   'Decline',
        comment:   'Comment (optional)',
        notify:    'Notify organiser',

        // §19.4's confirmation, asked only when there is something in it.
        confirm_notify:    'Will notify {people}.',
        confirm_conflicts: 'Conflicts with {events}.',

        answered:        'Answered',
        respond_failed:  'Couldn\'t send that answer',
    },
    drafts: {
        new_message:      'New message',
        none:             'No drafts',
        loading:          'Loading…',
        discard:          'Discard draft',
        close:            'Close',
        // The inline reply's own way to more room (func spec §23.6).
        expand:           'Expand to full screen',
        from:             'From',
        to:               'To',
        cc:               'Cc',
        bcc:              'Bcc',
        subject:          'Subject',
        body:             'Message',
        body_placeholder: 'Write your message…',

        // The two marker blocks inside the editor (func spec §11.5, §11.1). One
        // word each: they label a block that already looks different from the
        // owner's own text, and a label is all that block needs.
        signature_block: 'Signature',
        quoted:          'Quoted',
        // Func spec §11.2: the reply would go out from an address other than the
        // one the original was sent to. Both addresses, because which is which
        // is the whole of it.
        from_differs: 'Sends as {address}; the original was sent to {original}',

        // Func spec §11.3, asked at the click rather than at Send.
        confirm_reply_all: '{count} recipients. Reply to all?',

        saving:           'Saving…',
        saved:            'Saved',
        save_failed:      'Couldn\'t save — retrying',
        load_failed:      'Couldn\'t open that draft',
        editor_failed:    'Couldn\'t load the editor — reload the page',
        delete_failed:    'Couldn\'t discard that draft',

        // Recipients (func spec §11.3). A refused address is stated on the field
        // itself, not in a toast — it is one keystroke from being fixed.
        add_recipient:   'Add {address}',
        invalid_address: '"{address}" isn\'t an email address',
        exchanges:       '{count} messages',

        // Attachments on the send side (func spec §11.4).
        attach:           'Attach',
        attach_drop:      'Drop files to attach',
        attach_remove:    'Remove {name}',
        attach_dismiss:   'Dismiss',
        attach_failed:    'Couldn\'t attach that file',
        attach_too_large: 'Larger than 25 MB',

        // Word stems the attachment guard looks for at the start of a word (func
        // spec §11.3). Stems rather than whole words, so every form of each one
        // is covered without listing them — "attach" catches attached,
        // attaching and attachment; "enclos" catches enclosed and enclosing.
        //
        // A stem earns its place only if nothing ELSE starts with it: a guard
        // that cries wolf teaches the owner to dismiss it unread, which is the
        // one failure that makes it worse than having none. "append" was dropped
        // for exactly that — it fires on "appendix", which appears in the kind
        // of long document mail this guard is supposed to protect.
        attach_words: 'attach,enclos',

        // Terms the corporate-external guard looks for, matched the same way
        // (word start, comma-separated, multi-word terms allowed). They are the
        // guard's entire anti-false-alarm condition: mail leaving one's own
        // domain is most mail, so what makes THIS message worth a word is the
        // body saying it was meant to stay in.
        //
        // Short and blunt on purpose. Every term earns its place by appearing
        // almost only in mail that means it — "confidential" belongs, "private"
        // does not (private car park, private view), and "sensitive" does not
        // either (a sensitive subject is not a classification).
        internal_words: 'internal,confidential,do not forward,between us',

        // The §11.3 send guards, gathered onto one surface. None of them blocks:
        // each is dismissible, and "send anyway" is always there. Each line is
        // the finding itself — the dialog's title and its two buttons already say
        // what the list is for.
        guard_title:           'Before this goes out',
        guard_back:            'Back to the message',
        guard_send_anyway:     'Send anyway',
        guard_no_subject:      'No subject',
        guard_no_body:         'Empty message',
        guard_no_attachment:   'Mentions an attachment, but nothing is attached',
        guard_many_recipients: '{count} recipients',
        guard_uploading:       '{count} files still being attached',
        // Func spec §11.4: the destination server's limit, named — only where
        // the server has said what it is.
        guard_message_size:    '{size} — this server accepts up to {limit}',

        // The two guards that name addresses. Past three outside recipients the
        // list stops being readable at a glance and becomes a count — the
        // addresses are all still on the form behind the dialog.
        guard_internal_external:      'Body sounds internal — {addresses} outside {domain}',
        guard_internal_external_many: 'Body sounds internal — {count} recipients outside {domain}',
        guard_lookalike:              '{address} resembles {similar}',

        // Func spec §26: both versions are kept, the owner chooses, nothing is
        // discarded automatically.
        conflict:            'Edited somewhere else — both versions kept',
        conflict_keep_mine:  'Keep this version',
        conflict_load_other: 'Load the other version',

        // Quoting (func spec §11.1). The forward block reproduces the original's
        // headers; the attachments travel on the draft's manifest rather than in
        // this markup, so a forward carries them by default as §11.1 asks.
        quote_attribution: 'On {date}, {name} wrote:',
        forward_heading:   '---------- Forwarded message ----------',
        forward_date:      'Date',
    },
    outbox: {
        title:      'Outbox',
        empty:      'Nothing waiting to send',

        send:             'Send',
        send_later:       'Send later',
        no_recipients:    'Add at least one recipient',
        sent:             'Sent',
        scheduled:        'Scheduled for {time}',
        in_one_hour:      'In 1 hour',
        tomorrow_morning: 'Tomorrow morning (08:00)',
        pick_time:        'Pick a time…',
        send_at:          'Send at',
        schedule:         'Schedule',

        // Func spec §11.8: a scheduling message reads as what it is — the answer
        // and the meeting — rather than as the subject of a mail to an address.
        // The last line is for an entry whose meeting is not held here: the
        // method is all there is to say, and inventing a title would be worse.
        itip_acceptance:   'Acceptance of {title}',
        itip_decline:      'Decline of {title}',
        itip_tentative:    'Tentative for {title}',
        itip_reply:        'Reply to {title}',
        itip_update:       'Update to {title}',
        itip_cancellation: 'Cancellation of {title}',
        itip_other:        'Calendar reply',

        edit:    'Edit',
        retry:   'Try again',
        delete:  'Remove from outbox',
        dismiss: 'Dismiss',

        // Both confirmations state what happens to the copy the owner would
        // otherwise assume is gone — the one fact neither button carries.
        confirm_delete:           'Remove this message from the outbox? Your draft is kept.',
        confirm_delete_ambiguous: 'This may already have been sent — removing it drops the only record of the attempt. Your draft is kept.',

        // Func spec §11.8: what an entry is doing, in plain language. A temporary
        // failure is retried automatically and reads as pending, never as failed.
        state_sending_soon: 'Sending soon',
        state_scheduled:    'Scheduled for {time}',
        state_retrying:     'Waiting to retry',
        state_submitting:   'Sending…',
        state_sent:         'Sent',
        state_sent_late:    'Sent — later than scheduled',
        // Func spec §11.9: the schedule is honoured by this system, not the
        // server, so a message that waited for it to run says so when it goes.
        sent_late:          'Sent later than scheduled',
        state_cancelled:    'Not sent',
        attempts:           'attempt {count}',
        stuck:              'Still not sent — waiting since {age}',

        failure_permanent: 'The server refused this message',
        failure_auth:      'Sign-in expired — reconnect the account',
        failure_ambiguous: 'May or may not have been sent',

        already_queued:          'This message is already in the outbox',
        send_failed:             'Couldn\'t send — kept as a draft',
        undo_failed:             'Couldn\'t take that back',
        undo_already_sent:       'Too late — already sent',
        undo_already_submitting: 'Too late — already on its way',
        retry_failed:            'Couldn\'t try that message again',
        delete_failed:           'Couldn\'t remove that message from the outbox',
    },
    // The calendar (func spec §16–§20, §22, §25). Every string names a thing or
    // an action and stops: a grid is scanned, not read.
    calendar: {
        title:      'Calendar',
        event:      'Event',
        calendars:  'Calendars',
        calendar:   'Calendar',
        new_event:  'New event',
        edit_event: 'Edit event',
        close:      'Close',
        untitled:   'Untitled',
        all_day:    'All day',

        // The filter panel (§17.2).
        all_on:         'All',
        all_off:        'None',
        only_this_root: 'Only this account',
        no_calendars:   'No calendars yet',

        // Navigation (§17.1).
        previous:       'Previous',
        next:           'Next',
        today:          'Today',
        view_day:       'Day',
        view_week:      'Week',
        view_month:     'Month',
        view_agenda:    'Agenda',
        view_automatic: 'Automatic',
        agenda_from:    'From {date}',
        agenda_empty:   'Nothing scheduled',
        more_count:     '{count} more',

        // The editor (§18).
        summary:         'Title',
        starts:          'Starts',
        ends:            'Ends',
        location:        'Location',
        description:     'Description',
        attendees:       'Attendees',
        add_attendee:    'Invite {address}',
        becomes_meeting: 'Saving sends invitations',
        more:            'More',
        repeats:         'Repeats',
        repeat_none:     'Does not repeat',
        repeat_daily:    'Daily',
        repeat_weekly:   'Weekly',
        repeat_monthly:  'Monthly',
        repeat_yearly:   'Yearly',
        repeat_forever:  'Forever',
        repeat_count:    'After',
        repeat_until:    'Until',
        // §16.3's full grammar is wider than this editor writes; a rule it
        // cannot rewrite is kept as the server holds it, and that is worth one
        // line because the owner would otherwise expect the field to govern it.
        custom_rule:     'This repeat rule is kept as the server holds it',

        // Reminders (§18.4, §22.3).
        reminders:         'Reminders',
        reminder_at_start: 'At the start',
        reminder_before:   '{minutes} min before',
        minutes:           'min',

        end_before_start: 'The end is before the start',

        // Conflicts and the §19.4 confirmation.
        conflicts:          'Conflicts',
        what_changed:       'Changed',
        notified:           'Will be notified',
        nothing_to_notify:  'Nothing changes and nobody is notified',
        confirm_title:      'Confirm',
        confirm_update:     'Send this update?',
        confirm_cancel:     'Cancel this event?',
        confirm_respond:    'Send this response?',
        send_response:      'Send',

        // The three scopes (§16.3), asked every time.
        scope_this:         'This occurrence',
        scope_future:       'This and all future',
        scope_all:          'All occurrences',
        scope_edit_title:   'Which occurrences?',
        scope_cancel_title: 'Cancel which occurrences?',
        cancel_event:       'Cancel event',

        // The detail panel (§17.3, §20.2).
        your_response:     'Your response',
        response_comment:  'Comment',
        notify_organizer:  'Notify organizer',
        accept:            'Accept',
        tentative:         'Tentative',
        decline:           'Decline',
        linked_messages:   'Messages',
        message_invitation: 'Invitation',
        message_updated:    'Updated',
        message_cancelled:  'Cancelled',
        message_accepted:   'Accepted',
        message_declined:   'Declined',
        message_tentative:  'Tentative',
        message_replied:    'Replied',
        message_counter:    'New time proposed',
        in_zone:           '{time} {zone}',
        series_member:     'Part of a series',
        series_exception:  'Differs from its series',
        state_cancelled:   'Cancelled',
        state_declined:    'Declined',

        // Settings (§25).
        default_view:        'Default view',
        show_cancelled:      'Show cancelled and declined events',
        week_starts_monday:  'The week starts on Monday',
        default_reminder:    'Default reminder',

        // Reminders as they arrive (§22.3).
        reminder_starts_in: 'Starts in {minutes} min',
        reminder_open:      'Open',
        reminder_dismiss:   'Dismiss',

        // What an action did, and how to take it back (§12).
        undo:       'Undo',
        created:    'Event created',
        saved:      'Event saved',
        cancelled:  'Event cancelled',
        responded:  'Response sent',

        undo_failed:    'Couldn\'t take that back',
        create_failed:  'Couldn\'t create that event',
        save_failed:    'Couldn\'t save that change',
        cancel_failed:  'Couldn\'t cancel that event',
        respond_failed: 'Couldn\'t send that response',
        preview_failed: 'Couldn\'t work out what would change',
    },
    // Export (func spec §27, shown in Settings › Archive). Terse: the controls
    // name what they do. The two lines that say more are the honesty ones — how
    // much of an export has no body, and when its archive goes — because §27's
    // guarantee is only worth something if it can be checked.
    export: {
        title:      'Export',
        what:       'What',
        folder:     'Folder',
        account:    'Account',
        from:       'From',
        to:         'To',
        start:      'Start',
        download:   'Download',
        discard:    'Delete',
        empty:      'No exports',

        kind_everything: 'Everything',
        kind_account:    'One account',
        kind_folder:     'One folder',
        kind_range:      'A date range',

        range_label: '{from} – {to}',

        status_running:   '{written} of {total} messages',
        status_done:      '{messages} messages · {events} events',
        status_failed:    'Failed — {reason}',
        status_cancelled: 'Cancelled',
        status_expired:   'Archive deleted',

        missing: '{count} without a body',
        expires: 'until {date}',

        start_failed:   'Couldn\'t start that export',
        discard_failed: 'Couldn\'t delete that export',
    },
    // Notifications (func spec §22, §25's Notifications group). Terse: every row
    // is the thing it controls and stops. The one sentence that says more is the
    // §5.2.4 offer, which is a question and has to read as one.
    notifications: {
        title: 'Notifications',

        accounts:      'Accounts and folders',
        folders_count: '{count} folder | {count} folders',
        notify:        'Notify',
        silent:        'Silent',
        inherit:       'Inherit',
        default_is:    'Default — {mode}',

        senders: 'Senders',

        rules:               'Rules',
        direct_recipient:    'Notify when I am a direct recipient',
        bulk_silent:         'Silence mailing lists and bulk mail',
        recipient_threshold: 'Silence above this many recipients',

        show:         'Show in notifications',
        show_sender:  'Sender',
        show_subject: 'Subject',
        show_preview: 'Preview',

        quiet_hours:         'Quiet hours',
        quiet_hours_enabled: 'Hold notifications during a window',
        quiet_from:          'From',
        quiet_to:            'To',
        quiet_zone:          'Zone',
        zone_server:         'Server zone',

        event_reminders: 'Event reminders',

        offer:     'Notify for {folder}?',
        offer_yes: 'Yes',
        offer_no:  'No',

        save_failed: 'Couldn\'t save that notification rule',

        // The centre (func spec §22.3, §22.4).
        centre:        'Notifications',
        empty:         'Nothing to show',
        load_error:    'Couldn\'t load notifications',
        mark_all_read: 'Mark all read',
        show_silent:   'Silent',

        // One label per kind, for a row with neither a sender nor a title on it.
        kind_reminder:       'Reminder',
        kind_mail:           'New message',
        kind_invitation:     'Invitation',
        kind_event_updated:  'Meeting updated',
        kind_event_cancelled: 'Meeting cancelled',
        kind_send_failed:    'Message not sent',
        kind_needs_signin:   'Sign-in needed',
        kind_backfill_watch: 'Search ready',

        // Delivery, per browser (func spec §22.5).
        delivery:          'Delivery',
        desktop:           'Desktop notifications in this browser',
        push_unsupported:  'Not available in this browser',
        push_denied:       'Blocked in the browser settings',
        push_off:          'Off',
        push_on:           'On',
        push_failed:       'Couldn\'t turn desktop notifications on',
    },
}
